import React, { useState } from "react";
import { motion } from "framer-motion";
import ImageViewer from "../ui/ImageViewer";
import elValiente from "../../elValiente.jpeg";
import columpiaba from "../../columpiaba.png";
import voladoras from "../../voladoras.jpeg";
import yavalio from "../../yavalio.png";
import nomesuelto from "../../nomesuelto.jpeg";
import sinimposibles from "../../sinimposibles.jpeg";

// Datos ampliados de impresiones digitales para que el visor de imágenes tenga más información
const printItems = [
  {
    id: 1,
    title: "No me suelto",
    image: nomesuelto,
    technique: "Giclèe sobre papel de alta calidad",
    dimensions: "52 x 40 | 72 x 60 cm",
    year: "2025",
    price: "$1,780 | $2,850 MXN",
    description: "A veces me pesco de lo que sea para no caer al vacío.",
  },
  {
    id: 2,
    title: "El valiente",
    image: elValiente,
    technique: "Giclèe sobre papel de alta calidad",
    dimensions: "52 x 40 | 72 x 60 cm",
    year: "2025",
    price: "$1,780 | $2,850 MXN",
    description: "El que no se atreve, no se logra conocer del todo.",
  },
  {
    id: 3,
    title: "Voladoras",
    image: voladoras,
    technique: "Giclèe sobre papel de alta calidad",
    dimensions: "52 x 40 | 72 x 60 cm",
    year: "2025",
    price: "$1,780 | $2,850 MXN",
    description:
      "Divertirse en el aire, en el piso, donde sea, pero divertirse.",
  },
  {
    id: 4,
    title: "Un elefante se columpiaba",
    image: columpiaba,
    technique: "Giclèe sobre papel de alta calidad",
    dimensions: "52 x 40 | 72 x 60 cm",
    year: "2025",
    price: "$1,780 | $2,850 MXN",
    description: "El equilibrio no es destreza de pocos.",
  },
  {
    id: 5,
    title: "Ya valió",
    image: yavalio,
    technique: "Giclèe sobre papel de alta calidad",
    dimensions: "52 x 40 | 72 x 60 cm",
    year: "2025",
    price: "$1,780 | $2,850 MXN",
    description: "Puedo estar sostenida de lo insostenible.",
  },
];

const PrintsSection = () => {
  const getBaseName = (url) => {
    if (!url || typeof url !== "string") return null;
    const m = url.match(/([^\/]+?)(?:\.[0-9a-f]{6,})?\.(png|jpe?g|webp)$/i);
    if (m) return m[1];
    return null;
  };

  const buildSrcSet = (url, ext = "webp") => {
    const base = getBaseName(url);
    if (!base) return null;
    const sizes = [480, 800, 1200];
    return sizes.map((s) => `/resized/${base}-${s}.${ext} ${s}w`).join(", ");
  };
  // Estado para el visor de imágenes
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const openImageViewer = (index) => {
    setCurrentImageIndex(index);
    setIsViewerOpen(true);
  };

  const closeImageViewer = () => {
    setIsViewerOpen(false);
  };

  return (
    <section
      id="prints"
      className="py-20 bg-primary-dark relative overflow-hidden"
    >
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, margin: "-100px" }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-accent-light">
            ACROBACIAS
          </h2>
          <p className="text-lg text-accent-light text-opacity-80">
            Creo en divertirme, en lo imposible y en disfrutar el proceso, que
            la mayoría de las veces no se cómo terminará. “Acrobacias” es una
            colección que desafía la gravedad, la realidad y la coherencia.
            Obras impresas con la más alta calidad firmadas por el autor.
          </p>
        </motion.div>

        {/* Removed original single-grid view. Below is the custom 2x2 / vertical layout. */}
        {/* Layout: izquierda 2x2 (ids 1-4), derecha un vertical grande (id 5) */}
        <div className="flex flex-col md:flex-row gap-8">
          {/* Izquierda: 2x2 grid con los primeros 4 items */}
          <div className="w-full md:w-2/3 grid grid-cols-1 md:grid-cols-2 gap-8">
            {printItems.slice(0, 4).map((item, idx) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: idx * 0.06 }}
                viewport={{ once: true, margin: "-100px" }}
                whileHover={{
                  y: -8,
                  boxShadow: "0 18px 40px -12px rgba(0,0,0,0.22)",
                }}
                className="bg-primary-light rounded-xl overflow-hidden shadow-lg border border-accent border-opacity-10 transition-all duration-400 cursor-pointer"
                onClick={() => openImageViewer(idx)}
              >
                <div className="overflow-hidden bg-gray-300 h-48 md:h-[260px] flex items-center justify-center">
                  <picture className="w-full h-full">
                    {(() => {
                      const webpSet = buildSrcSet(item.image, "webp");
                      const jpgFallback = (() => {
                        const b = getBaseName(item.image);
                        return b ? `/resized/${b}-800.jpg` : item.image;
                      })();
                      return (
                        <>
                          {webpSet && (
                            <source
                              type="image/webp"
                              srcSet={webpSet}
                              sizes="100vw"
                            />
                          )}
                          <img
                            src={jpgFallback}
                            alt={item.title}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                          />
                        </>
                      );
                    })()}
                  </picture>
                </div>
                <div className="p-4 flex flex-col">
                  <h3 className="text-lg font-bold mb-1 text-accent-light">
                    {item.title}
                  </h3>
                  <p className="text-accent-light text-opacity-70 mb-2 text-sm line-clamp-2">
                    {item.technique}
                  </p>
                  <div className="mt-auto flex justify-between items-center">
                    <span className="text-xs font-medium text-accent-light">
                      {item.dimensions}
                    </span>
                    <span className="text-accent font-bold text-sm">
                      {item.price}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Derecha: una sola imagen vertical (el quinto item) */}
          <div className="w-full md:w-1/3 h-auto">
            {printItems[4] && (
              <motion.div
                key={printItems[4].id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true, margin: "-100px" }}
                whileHover={{
                  y: -6,
                  boxShadow: "0 22px 48px -14px rgba(0,0,0,0.24)",
                }}
                className="bg-primary-light rounded-xl overflow-hidden shadow-lg border border-accent border-opacity-10 transition-all duration-400 cursor-pointer"
                onClick={() => openImageViewer(4)}
              >
                <div className="overflow-hidden bg-gray-300 h-72 md:h-[520px] flex items-center justify-center">
                  <picture className="w-full h-full">
                    {(() => {
                      const webpSet = buildSrcSet(printItems[4].image, "webp");
                      const jpgFallback = (() => {
                        const b = getBaseName(printItems[4].image);
                        return b
                          ? `/resized/${b}-1200.jpg`
                          : printItems[4].image;
                      })();
                      return (
                        <>
                          {webpSet && (
                            <source
                              type="image/webp"
                              srcSet={webpSet}
                              sizes="(min-width:1024px) 33vw, 80vw"
                            />
                          )}
                          <img
                            src={jpgFallback}
                            alt={printItems[4].title}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-contain transition-transform duration-500 hover:scale-102"
                          />
                        </>
                      );
                    })()}
                  </picture>
                </div>
                <div className="p-6 flex flex-col">
                  <h3 className="text-xl font-bold mb-2 text-accent-light">
                    {printItems[4].title}
                  </h3>
                  <p className="text-accent-light text-opacity-70 mb-4 line-clamp-2">
                    {printItems[4].technique}
                  </p>
                  <div className="mt-auto flex justify-between items-center">
                    <span className="text-sm font-medium text-accent-light">
                      {printItems[4].dimensions}
                    </span>
                    <span className="text-accent font-bold">
                      {printItems[4].price}
                    </span>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* Visor de imágenes */}
      <ImageViewer
        isOpen={isViewerOpen}
        onClose={closeImageViewer}
        images={printItems}
        currentIndex={currentImageIndex}
        setCurrentIndex={setCurrentImageIndex}
      />

      {/* Background decorative elements */}
      <motion.div
        className="absolute -right-20 top-20 w-80 h-80 rounded-full bg-accent opacity-5 blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.05, 0.07, 0.05],
          rotate: [0, 90, 0],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          repeatType: "reverse",
        }}
      />
      <motion.div
        className="absolute -left-40 bottom-40 w-96 h-96 rounded-full bg-accent opacity-5 blur-3xl"
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.05, 0.08, 0.05],
          rotate: [0, -90, 0],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          repeatType: "reverse",
          delay: 5,
        }}
      />
    </section>
  );
};

export default PrintsSection;
